import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAppTheme } from '../../contexts/ThemeContext';
import { agentService } from '../../services/agentService.js';
import Icon from '../Icon';
import { isAdmin } from '../../utils/permissions.js';

const ADMIN_ONLY_SIDEBAR_SECTIONS = new Set(['administration', 'sellers', 'development']);

const getScrollbarStyles = (theme) => `
  .sidebar-container::-webkit-scrollbar {
    width: ${theme.scrollbar.width};
  }

  .sidebar-container::-webkit-scrollbar-track {
    background: ${theme.scrollbar.track};
  }

  .sidebar-container::-webkit-scrollbar-thumb {
    background: ${theme.scrollbar.thumb};
    border-radius: ${theme.borderRadius.sm};
  }

  .sidebar-container::-webkit-scrollbar-thumb:hover {
    background: ${theme.scrollbar.thumbHover};
  }

  .sidebar-container {
    scrollbar-width: thin;
    scrollbar-color: ${theme.scrollbar.thumb} ${theme.scrollbar.track};
  }

  .sidebar-section-content::-webkit-scrollbar {
    width: ${theme.scrollbar.widthSmall};
  }

  .sidebar-section-content::-webkit-scrollbar-track {
    background: ${theme.scrollbar.trackSmall};
  }

  .sidebar-section-content::-webkit-scrollbar-thumb {
    background: ${theme.scrollbar.thumb};
    border-radius: 3px;
  }

  .sidebar-section-content::-webkit-scrollbar-thumb:hover {
    background: ${theme.scrollbar.thumbHover};
  }

  .sidebar-section-content {
    scrollbar-width: thin;
    scrollbar-color: ${theme.scrollbar.thumb} ${theme.scrollbar.trackSmall};
  }
`;

const getSidebarStyles = (theme) => ({
  accordion: {
    backgroundColor: theme.colors.background.sidebar,
    boxShadow: 'none',
    '&:before': { display: 'none' },
    '&.Mui-expanded': {
      margin: '0 0 16px 0',
      borderRadius: theme.borderRadius.sm,
    },
  },
  accordionSummary: {
    backgroundColor: theme.colors.background.main,
    borderRadius: theme.borderRadius.sm,
    marginBottom: theme.spacing.xs,
    minHeight: '48px',
    '&.Mui-expanded': { minHeight: '48px' },
    '& .MuiAccordionSummary-content': { margin: `${theme.spacing.sm} 0` },
    '&:hover': { backgroundColor: alpha(theme.colors.background.main, 0.8) },
  },
  accordionDetails: {
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.background.sidebar,
    borderRadius: `0 0 ${theme.borderRadius.sm} ${theme.borderRadius.sm}`,
    marginTop: 0,
    maxHeight: '400px',
    overflowY: 'auto',
  },
  listItemButton: (isActive) => ({
    borderRadius: theme.borderRadius.sm,
    marginBottom: '2px',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    color: theme.colors.text.primary,
    '&:hover': {
      backgroundColor: isActive ? theme.colors.primary : theme.colors.background.main,
    },
    '&.Mui-selected': {
      backgroundColor: theme.colors.primary,
      color: theme.colors.text.primary,
      '&:hover': { backgroundColor: theme.colors.primary },
    },
  }),
  circularProgress: {
    color: theme.colors.accent,
    width: '16px !important',
    height: '16px !important',
  },
  alert: {
    backgroundColor: `${theme.colors.error.background}1a`,
    color: theme.colors.error.text,
    border: `1px solid ${theme.colors.error.border}`,
    '& .MuiAlert-icon': { color: theme.colors.error.text },
  },
});

/**
 * Sidebar Configuration
 * Defines all sections and their route-based visibility rules
 */
const SIDEBAR_CONFIG = {
  sections: [
    {
      id: 'ad-manager',
      title: 'Advertisers',
      icon: 'ad-manager',
      routePatterns: ['/briefs', '/media-buys', '/creatives', '/sellers', '/products', '/packages', '/sessions'],
      items: [
        { id: 'ad-manager-briefs', label: 'Briefs', icon: 'description', route: '/briefs' },
        { id: 'ad-manager-new-brief', label: 'Create Brief', icon: 'add', route: '/briefs/new' },
        { id: 'ad-manager-media-buys', label: 'Media Buys', icon: 'campaign', route: '/media-buys' },
        { id: 'ad-manager-creatives', label: 'Creatives', icon: 'image', route: '/creatives' },
      ],
    },
    {
      id: 'sellers',
      title: 'Sellers',
      icon: 'store',
      routePatterns: ['/administration'], // Show when route starts with this
      items: [
        { id: 'sellers-sellers', label: 'Sellers', icon: 'store', route: '/administration/sellers' },
        { id: 'sellers-products', label: 'Products', icon: 'inventory', route: '/administration/sellers/products' },
        { id: 'sellers-packages', label: 'Packages', icon: 'package', route: '/administration/sellers/packages' },
      ],
    },
    {
      id: 'administration',
      title: 'Administration',
      icon: 'admin',
      routePatterns: ['/administration'], // Show when route starts with this
      exclusive: true, // When active, only show this section
      items: [
        { id: 'administration-accounts', label: 'Accounts', icon: 'accounts', route: '/administration/accounts' },
        { id: 'administration-roles', label: 'Roles', icon: 'roles', route: '/administration/roles' },
        { id: 'administration-policies', label: 'Policies', icon: 'policies', route: '/administration/policies' },
      ],
    },
    {
      id: 'development',
      title: 'Development',
      icon: 'code',
      routePatterns: ['/development'], // Show when route starts with this
      items: [
        { id: 'development-components', label: 'Components', icon: 'palette', route: '/development/components' },
        {
          id: 'development-verve-plus-grid',
          label: 'Verve Plus Grid',
          icon: 'grid',
          route: '/development/verve-plus-grid',
        },
        { id: 'development-agent-flow', label: 'Agent Flow', icon: 'flow', route: '/development/agent-flow' },
        {
          id: 'development-worker-status',
          label: 'Worker Status',
          icon: 'settings',
          route: '/development/worker-status',
        },
      ],
    },
    {
      id: 'agents',
      title: 'Agents',
      icon: 'robot',
      routePatterns: ['/agents'], // Show when route includes these
      items: [
        { id: 'agents-list', label: 'Agents', icon: 'build', route: '/agents' },
        { id: 'agents-studio', label: 'Agent Studio', icon: 'flow', route: '/agents/studio' },
        { id: 'agents-sessions', label: 'Agent Sessions', icon: 'chat', route: '/agents/sessions' },
      ],
    },
    {
      id: 'mcp',
      title: 'MCP',
      icon: 'tools',
      routePatterns: ['/agents'], // Show when route includes these
      items: [
        { id: 'mcp-servers', label: 'MCP Servers', icon: 'cloud', route: '/agents/mcp-servers' },
        { id: 'mcp-tools', label: 'MCP Tools', icon: 'tools', route: '/agents/mcp-servers/tools' },
      ],
    },
  ],
};

export const getVisibleSections = (pathname, user) => {
  const filterByRole = (sections) => {
    if (isAdmin(user)) return sections;
    return sections.filter((s) => !ADMIN_ONLY_SIDEBAR_SECTIONS.has(s.id));
  };

  if (pathname.startsWith('/administration')) {
    const adminSection = SIDEBAR_CONFIG.sections.find((s) => s.id === 'administration');
    const sellersSection = SIDEBAR_CONFIG.sections.find((s) => s.id === 'sellers');
    return filterByRole([adminSection, sellersSection].filter(Boolean));
  }

  if (pathname.startsWith('/agents')) {
    const agentsSection = SIDEBAR_CONFIG.sections.find((s) => s.id === 'agents');
    const mcpSection = SIDEBAR_CONFIG.sections.find((s) => s.id === 'mcp');
    return [agentsSection, mcpSection].filter(Boolean);
  }

  const exclusiveSection = SIDEBAR_CONFIG.sections.find(
    (section) => section.exclusive && section.routePatterns.some((pattern) => pathname.startsWith(pattern)),
  );

  if (exclusiveSection) {
    return filterByRole([exclusiveSection]);
  }

  return filterByRole(
    SIDEBAR_CONFIG.sections.filter((section) => {
      return section.routePatterns.some((pattern) => {
        if (pattern.startsWith('/')) {
          return pathname.startsWith(pattern);
        }
        return pathname.includes(pattern);
      });
    }),
  );
};

const Sidebar = ({ activeSection, onSectionChange, authToken, user }) => {
  const { theme } = useAppTheme();
  const scrollbarStyles = useMemo(() => getScrollbarStyles(theme), [theme]);
  const styles = useMemo(() => getSidebarStyles(theme), [theme]);
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState(null);

  // Inject scrollbar styles
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = scrollbarStyles;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, [scrollbarStyles]);

  const visibleSections = useMemo(() => getVisibleSections(location.pathname, user), [location.pathname, user]);

  /**
   * Auto-expand sections based on current route
   */
  useEffect(() => {
    const currentPath = location.pathname;
    const sectionsToExpand = new Set();

    visibleSections.forEach((section) => {
      // Check if any route pattern matches
      const matches = section.routePatterns.some((pattern) => {
        if (pattern.startsWith('/')) {
          return currentPath.startsWith(pattern);
        }
        return currentPath.includes(pattern);
      });

      if (matches) {
        sectionsToExpand.add(section.id);
      }
    });

    setExpandedSections(Array.from(sectionsToExpand));
  }, [location.pathname, visibleSections]);

  const handleAccordionChange = (sectionId) => (event, isExpanded) => {
    setExpandedSections((prev) => (isExpanded ? [...prev, sectionId] : prev.filter((id) => id !== sectionId)));
  };

  // Fetch agents from API
  useEffect(() => {
    const fetchAgents = async () => {
      if (!authToken) {
        return;
      }

      setAgentsLoading(true);
      setAgentsError(null);

      try {
        const result = await agentService.getAgents(authToken);

        if (result.ok && result.data) {
          setAgents(result.data);
        } else {
          console.error('Failed to fetch agents:', result.error);
          setAgentsError(result.error?.message || 'Failed to fetch agents');
        }
      } catch (error) {
        console.error('Error fetching agents:', error);
        setAgentsError('Error fetching agents');
      } finally {
        setAgentsLoading(false);
      }
    };

    fetchAgents();
  }, [authToken]);

  // Get appropriate icon for agent category
  const getAgentIcon = (category) => {
    const iconMap = {
      support: 'support',
      development: 'development',
      creative: 'creative',
      analytics: 'analytics',
      chatbot: 'robot',
      assistant: 'assistant',
      default: 'robot',
    };
    return iconMap[category] || iconMap.default;
  };

  // Transform agents data for sidebar display
  const transformAgentsToItems = (agents) => {
    return agents.map((agent) => ({
      id: agent.id,
      label: agent.name || agent.id,
      icon: getAgentIcon(agent.category || agent.type),
      category: agent.category,
      description: agent.description,
      route: `/agents/${agent.id}`,
    }));
  };

  const handleItemClick = (itemId, item) => {
    // Don't trigger section change for loading or error states
    if (itemId === 'loading' || itemId === 'error') {
      return;
    }

    // Navigate to route if item has one
    if (item && item.route) {
      navigate(item.route);
      return;
    }

    // Check if this is an agent ID
    if (agents.some((agent) => agent.id === itemId)) {
      const agentUrl = `/agents/${itemId}`;
      navigate(agentUrl);
      return;
    }

    // Handle section changes for everything else
    if (onSectionChange) {
      onSectionChange(itemId);
    }
  };

  const renderSectionItems = (items) => {
    const safeItems = Array.isArray(items) ? items.filter(Boolean) : [];
    const currentPath = location.pathname;

    // First, find exact matches
    const exactMatch = safeItems.find((item) => item.route && currentPath === item.route);

    return safeItems.map((item) => {
      // Determine if item is active based on route
      let isActive = false;

      if (item.route) {
        // Prioritize exact matches - if there's an exact match, only that item is active
        if (exactMatch) {
          isActive = currentPath === item.route;
        } else {
          // If no exact match, check if path starts with route (for nested routes)
          // But only if the route is a prefix of the current path
          isActive = currentPath === item.route || currentPath.startsWith(item.route + '/');
        }
      } else {
        // Fallback to activeSection
        isActive = activeSection === item.id;
      }

      if (item.id === 'loading') {
        return (
          <ListItem key={item.id} disablePadding>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2, width: '100%' }}>
              <CircularProgress size={16} sx={styles.circularProgress} />
              <Typography variant="body2" sx={{ ml: 1, color: theme.colors.text.secondary }}>
                {item.label}
              </Typography>
            </Box>
          </ListItem>
        );
      }

      if (item.id === 'error') {
        return (
          <ListItem key={item.id} disablePadding>
            <Alert severity="error" sx={{ ...styles.alert, width: '100%', fontSize: '0.75rem' }}>
              {item.label}
            </Alert>
          </ListItem>
        );
      }

      return (
        <ListItem key={item.id} disablePadding>
          <ListItemButton
            selected={isActive}
            onClick={() => handleItemClick(item.id, item)}
            sx={styles.listItemButton(isActive)}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Icon
                name={item.icon}
                size="small"
                style={{
                  color: isActive ? theme.colors.text.primary : theme.colors.text.secondary,
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: isActive ? 500 : 400,
              }}
            />
          </ListItemButton>
        </ListItem>
      );
    });
  };

  /**
   * Get items for a section (static or dynamic)
   */
  const getSectionItems = (section) => {
    if (section.dynamicItems) {
      // Handle dynamic items (e.g., agents)
      if (section.id === 'agents') {
        if (agentsLoading) {
          return [{ id: 'loading', label: 'Loading agents...', icon: 'loading' }];
        }
        if (agentsError) {
          return [{ id: 'error', label: 'Error loading agents', icon: 'error' }];
        }
        return transformAgentsToItems(agents);
      }
      return [];
    }
    return section.items || [];
  };

  return (
    <Box
      className="sidebar-container"
      sx={{
        p: '10px',
        height: '100%',
        overflowY: 'auto',
        backgroundColor: theme.colors.background.sidebar,
      }}
    >
      {visibleSections.map((section) => {
        const items = getSectionItems(section);

        return (
          <Accordion
            key={section.id}
            expanded={expandedSections.includes(section.id)}
            onChange={handleAccordionChange(section.id)}
            sx={{ ...styles.accordion, marginBottom: '16px' }}
            disableGutters
          >
            <AccordionSummary
              sx={styles.accordionSummary}
              expandIcon={
                expandedSections.includes(section.id) ? (
                  <ExpandLessIcon sx={{ fontSize: 16, color: theme.colors.text.secondary }} />
                ) : (
                  <ExpandMoreIcon sx={{ fontSize: 16, color: theme.colors.text.secondary }} />
                )
              }
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Icon
                  name={section.icon}
                  size="small"
                  style={{
                    marginRight: theme.spacing.sm,
                    color: theme.colors.text.primary,
                  }}
                />
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: theme.colors.text.primary,
                    fontSize: '16px',
                    fontWeight: theme.fontWeight.normal,
                  }}
                >
                  {section.title}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails className="sidebar-section-content" sx={{ ...styles.accordionDetails, p: '0' }}>
              <List
                dense
                disablePadding
                sx={{
                  backgroundColor: theme.colors.background.sidebar,
                }}
              >
                {renderSectionItems(items)}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default Sidebar;
